import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';

/**
 * An hour/minute picker with the minutes we actually want.
 *
 * `<input type="time" step={300}>` was supposed to do this, but the native
 * dropdown ignores the step and lists every minute - 45, 46, 47 - which is
 * both unusable and not how anyone describes a shift. Two selects behave
 * identically in every browser.
 *
 * Also beats one list of 288 five-minute options, which is what this editor
 * had: picking 14:35 meant scrolling past a hundred and seventy entries.
 * Hours first, then minutes, is two short lists.
 *
 * A value that is not on the grid (older data, or hours set elsewhere) is
 * added as an option rather than silently snapped, so opening an editor
 * cannot quietly change what is stored.
 */

interface TimeSelectProps {
  label: string;
  value: string;                 // "09:00" or "09:00:00"
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Minutes between options. */
  stepMinutes?: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** The browser's own select, styled to the popup.
 *
 *  MUI's TextField wraps a select in a notched outline and paints a second
 *  arrow over the native one, which is what made two of these fill the
 *  dialog. A bare select keeps the native behaviour this component exists
 *  for and takes only the width it needs. */
const BOX_SELECT = {
  appearance: 'auto',
  backgroundColor: POPUP_COLORS.fieldBackground,
  color: POPUP_COLORS.titleText,
  border: `1px solid ${POPUP_COLORS.background}`,
  borderRadius: '8px',
  padding: '6px 8px',
  fontSize: '0.95rem',
  minWidth: 64,
  cursor: 'pointer',
  '&:focus': { outline: `2px solid ${POPUP_COLORS.titleText}`, outlineOffset: 0 },
  '&:disabled': { opacity: 0.5, cursor: 'default' },
} as const;

export const TimeSelect: React.FC<TimeSelectProps> = ({
  label, value, onChange, disabled = false, stepMinutes = 5,
}) => {
  const [hh, mm] = (value || '00:00').slice(0, 5).split(':');

  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, i) => pad(i)), []);

  const minutes = useMemo(() => {
    const list: string[] = [];
    for (let m = 0; m < 60; m += stepMinutes) list.push(pad(m));
    // Keep an off-grid value selectable instead of losing it.
    if (mm && !list.includes(mm)) {
      list.push(mm);
      list.sort();
    }
    return list;
  }, [stepMinutes, mm]);

  return (
    // Label BESIDE the fields, on the shared fieldRow/fieldLabel tokens, so
    // this reads like every other row in a clinic dialog rather than a
    // stacked form of its own.
    <Box sx={CLINIC_DIALOG_SX.fieldRow}>
      <Typography sx={CLINIC_DIALOG_SX.fieldLabel}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Native selects, not MUI's menu. The reason this component
            exists is that plain selects behave identically everywhere; a
            portalled menu for a 12-item list adds a popup to close and
            cannot be driven by a change event, so it is also untestable.
            Sized to their contents - a full-width dropdown for "09" is
            mostly empty space. */}
        <Box component="select" value={hh} disabled={disabled}
             aria-label={`${label} hour`} sx={BOX_SELECT}
             onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
               onChange(`${e.target.value}:${mm}`)}>
          {hours.map((h) => <option key={h} value={h}>{h}</option>)}
        </Box>
        <Typography sx={{ color: POPUP_COLORS.titleText }}>:</Typography>
        <Box component="select" value={mm} disabled={disabled}
             aria-label={`${label} minute`} sx={BOX_SELECT}
             onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
               onChange(`${hh}:${e.target.value}`)}>
          {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
        </Box>
      </Box>
    </Box>
  );
};

export default TimeSelect;
